/**
 * TypeScript type definitions for v2 camera settings structure (hardware_revision >= 2.0)
 * Based on SC-410 Configuration API v2.0 OpenAPI specification
 * Mirrors the settings JSON structure from remote-management-api.json
 */

/**
 * Main settings interface containing all configuration categories
 */
export interface CameraSettingsV2 {
  general: GeneralSettingsV2;
  operation_mode: OperationModeSettingsV2;
  irled: IrledSettingsV2;
  pir: PirSettingsV2;
  lte: LteSettingsV2;
  smtp: SmtpSettingsV2;
  wifi: WifiSettingsV2;
}

/**
 * General settings category
 */
export interface GeneralSettingsV2 {
  battery_type: number; // 0=UNKNOWN, 1=DC, 2=ALKALINE, 3=NIMH, 4=LITHIUM, 5=LITHIUM_ION
  disable_suspend: boolean; // Prevents deepsleep mode
  log_to_sdcard: boolean; // TRUE=retain captures, FALSE=delete after transmission
  endpoint_url: string; // SW9.4 - Endpoint URL for Commercial/CBP messages
  rtc_timeout_seconds: number; // 0 or 7200-86400, resolution: 60 seconds
  api_connection_timeout_seconds: number; // 30-3600
  verbose_debug: boolean; // Enable detailed debug logs
  rtc_trigger_capture: boolean; // SW9.12 - Capture on status report wake
}

/**
 * Operation mode settings category
 */
export interface OperationModeSettingsV2 {
  name: string; // Operating Mode Name
  max_iso_day: number; // SW9.6 - 100-1600
  max_iso_night: number; // SW9.5 - 100-1600
  max_shutter_time_day: number; // 0-1000000 microseconds
  max_shutter_time_night: number; // 0-1000000 microseconds
  capture_jpeg_quality: number; // 0-100
  capture_count: number; // SW9.8 - 1-10
  capture_delay_ms: number; // SW9.9 - 100-10000
  process_sendmail: boolean; // Enable/disable all SMTP messages
  capture_mode: number; // 1=CAPTURE_MODE_4000x3000, 2=CAPTURE_MODE_1600x1200
  trigger_black_out_period_ms: number; // SW9.3 - 0-50000
  email_status_report: boolean; // Enable SMTP health & status messages
}

/**
 * IR LED settings category
 */
export interface IrledSettingsV2 {
  duty_cycle_still_cap: number; // 1-100 PWM duty cycle
}

/**
 * PIR sensor settings category
 */
export interface PirSettingsV2 {
  pir_sens: number; // 32-200, smaller = more sensitive
  pir_ignored: boolean; // Completely disable PIR sensor
  blind_time: number; // 0-15, avoid retriggering 0.5-8 seconds
  pulse_counter: number; // 0-3, pulses above threshold
  window_time: number; // 0-3, pulse counter evaluation time
  operation_mode: number; // 0-3, readout/wakeup mode selection
  filter_source: number; // 0-3, ADC output source
  disable_pir_cron: string[]; // SW9.2 - Cron strings for PIR disable
  enable_pir_cron: string[]; // SW9.1 - Cron strings for PIR enable
}

/**
 * LTE network configuration settings category
 */
export interface LteSettingsV2 {
  apn: string; // SW9.10 - Access Point Name
  username: string; // SW9.10 - APN credentials
  password: string; // SW9.10 - APN credentials
  lte_ant_index: number; // 1=ANT_INTERNAL, 2=ANT_EXTERNAL
  wake_on_wan: boolean; // SW9.13 - Enable Wake on WWAN (note: protobuf uses wake_on_wwan)
  gps_enable: boolean; // Use GPS from LTE Module GNSS Receiver
  reboot_delay: number; // SW9.16 - 15-1440 minutes
  sim_index?: number | null; // 0-3, optional
}

/**
 * SMTP (email) configuration settings category
 */
export interface SmtpSettingsV2 {
  host: string; // SMTP Server URL
  port: number; // 1-65535
  username: string; // SMTP server credentials
  password: string; // SMTP server credentials
  from: string; // Sender email address
  real_name: string; // Human readable sender name
  to_addr: string[]; // Recipients on "To:" line
  to_cc: string[]; // Recipients on "CC:" line
  to_bcc: string[]; // Recipients on "BCC:" line
}

/**
 * WiFi configuration settings category
 */
export interface WifiSettingsV2 {
  sta_ssid: string; // SW9.14 - SSID for WiFi Access Point
  sta_password: string; // SW9.15 - WiFi Access Point password
  wifi_ant_index: number; // 1=ANT_INTERNAL, 2=ANT_EXTERNAL
}

/**
 * Enums for type-safe selection values
 */
export enum BatteryType {
  UNKNOWN = 0,
  DC = 1,
  ALKALINE = 2,
  NIMH = 3,
  LITHIUM = 4,
  LITHIUM_ION = 5,
}

export enum CaptureMode {
  CAPTURE_MODE_4000x3000 = 1,
  CAPTURE_MODE_1600x1200 = 2,
}

export enum AntennaIndex {
  ANT_INTERNAL = 1,
  ANT_EXTERNAL = 2,
}

/**
 * Flattened form fields for simplified form binding
 * These will be mapped to/from the nested CameraSettingsV2 structure
 */
export interface CameraFormFieldsV2 {
  // General
  battery_type: number;
  disable_suspend: boolean;
  log_to_sdcard: boolean;
  endpoint_url: string;
  rtc_timeout_seconds: number;
  api_connection_timeout_seconds: number;
  verbose_debug: boolean;
  rtc_trigger_capture: boolean;

  // Operation Mode
  operation_mode_name: string;
  max_iso_day: number;
  max_iso_night: number;
  max_shutter_time_day: number;
  max_shutter_time_night: number;
  capture_jpeg_quality: number;
  capture_count: number;
  capture_delay_ms: number;
  process_sendmail: boolean;
  capture_mode: number;
  trigger_black_out_period_ms: number;
  email_status_report: boolean;

  // IR LED
  duty_cycle_still_cap: number;

  // PIR
  pir_sens: number;
  pir_ignored: boolean;
  blind_time: number;
  pulse_counter: number;
  window_time: number;
  pir_operation_mode: number;
  filter_source: number;
  disable_pir_cron: string; // Comma-separated
  enable_pir_cron: string; // Comma-separated

  // LTE
  apn: string;
  lte_username: string;
  lte_password: string;
  lte_ant_index: number;
  wake_on_wan: boolean;
  gps_enable: boolean;
  reboot_delay: number;
  sim_index: number | null;

  // SMTP
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  smtp_from: string;
  smtp_real_name: string;
  smtp_to_addr: string; // Comma-separated
  smtp_to_cc: string; // Comma-separated
  smtp_to_bcc: string; // Comma-separated

  // WiFi
  sta_ssid: string;
  sta_password: string;
  wifi_ant_index: number;
}

/**
 * Default values matching typical camera configuration
 */
export const DEFAULT_FORM_VALUES_V2: CameraFormFieldsV2 = {
  // General
  battery_type: BatteryType.UNKNOWN,
  disable_suspend: false,
  log_to_sdcard: true,
  endpoint_url: '',
  rtc_timeout_seconds: 0,
  api_connection_timeout_seconds: 300,
  verbose_debug: false,
  rtc_trigger_capture: false,

  // Operation Mode
  operation_mode_name: 'Default',
  max_iso_day: 800,
  max_iso_night: 1600,
  max_shutter_time_day: 2000,
  max_shutter_time_night: 1000000,
  capture_jpeg_quality: 85,
  capture_count: 1,
  capture_delay_ms: 100,
  process_sendmail: false,
  capture_mode: CaptureMode.CAPTURE_MODE_4000x3000,
  trigger_black_out_period_ms: 0,
  email_status_report: false,

  // IR LED
  duty_cycle_still_cap: 50,

  // PIR
  pir_sens: 64,
  pir_ignored: false,
  blind_time: 2,
  pulse_counter: 1,
  window_time: 2,
  pir_operation_mode: 2,
  filter_source: 1,
  disable_pir_cron: '',
  enable_pir_cron: '',

  // LTE
  apn: '',
  lte_username: '',
  lte_password: '',
  lte_ant_index: AntennaIndex.ANT_INTERNAL,
  wake_on_wan: false,
  gps_enable: false,
  reboot_delay: 60,
  sim_index: null,

  // SMTP
  smtp_host: '',
  smtp_port: 587,
  smtp_username: '',
  smtp_password: '',
  smtp_from: '',
  smtp_real_name: '',
  smtp_to_addr: '',
  smtp_to_cc: '',
  smtp_to_bcc: '',

  // WiFi
  sta_ssid: '',
  sta_password: '',
  wifi_ant_index: AntennaIndex.ANT_INTERNAL,
};

/**
 * Helper function to convert nested v2 settings to flat form fields
 */
export function settingsToFormFields(settings: CameraSettingsV2): CameraFormFieldsV2 {
  return {
    // General
    battery_type: settings.general.battery_type,
    disable_suspend: settings.general.disable_suspend,
    log_to_sdcard: settings.general.log_to_sdcard,
    endpoint_url: settings.general.endpoint_url,
    rtc_timeout_seconds: settings.general.rtc_timeout_seconds,
    api_connection_timeout_seconds: settings.general.api_connection_timeout_seconds,
    verbose_debug: settings.general.verbose_debug,
    rtc_trigger_capture: settings.general.rtc_trigger_capture,

    // Operation Mode
    operation_mode_name: settings.operation_mode.name,
    max_iso_day: settings.operation_mode.max_iso_day,
    max_iso_night: settings.operation_mode.max_iso_night,
    max_shutter_time_day: settings.operation_mode.max_shutter_time_day,
    max_shutter_time_night: settings.operation_mode.max_shutter_time_night,
    capture_jpeg_quality: settings.operation_mode.capture_jpeg_quality,
    capture_count: settings.operation_mode.capture_count,
    capture_delay_ms: settings.operation_mode.capture_delay_ms,
    process_sendmail: settings.operation_mode.process_sendmail,
    capture_mode: settings.operation_mode.capture_mode,
    trigger_black_out_period_ms: settings.operation_mode.trigger_black_out_period_ms,
    email_status_report: settings.operation_mode.email_status_report,

    // IR LED
    duty_cycle_still_cap: settings.irled.duty_cycle_still_cap,

    // PIR
    pir_sens: settings.pir.pir_sens,
    pir_ignored: settings.pir.pir_ignored,
    blind_time: settings.pir.blind_time,
    pulse_counter: settings.pir.pulse_counter,
    window_time: settings.pir.window_time,
    pir_operation_mode: settings.pir.operation_mode,
    filter_source: settings.pir.filter_source,
    disable_pir_cron: settings.pir.disable_pir_cron.join(', '),
    enable_pir_cron: settings.pir.enable_pir_cron.join(', '),

    // LTE
    apn: settings.lte.apn,
    lte_username: settings.lte.username,
    lte_password: settings.lte.password,
    lte_ant_index: settings.lte.lte_ant_index,
    wake_on_wan: settings.lte.wake_on_wan,
    gps_enable: settings.lte.gps_enable,
    reboot_delay: settings.lte.reboot_delay,
    sim_index: settings.lte.sim_index ?? null,

    // SMTP
    smtp_host: settings.smtp.host,
    smtp_port: settings.smtp.port,
    smtp_username: settings.smtp.username,
    smtp_password: settings.smtp.password,
    smtp_from: settings.smtp.from,
    smtp_real_name: settings.smtp.real_name,
    smtp_to_addr: settings.smtp.to_addr.join(', '),
    smtp_to_cc: settings.smtp.to_cc.join(', '),
    smtp_to_bcc: settings.smtp.to_bcc.join(', '),

    // WiFi
    sta_ssid: settings.wifi.sta_ssid,
    sta_password: settings.wifi.sta_password,
    wifi_ant_index: settings.wifi.wifi_ant_index,
  };
}

/**
 * Helper function to convert flat form fields to nested v2 settings
 */
export function formFieldsToSettings(fields: CameraFormFieldsV2): CameraSettingsV2 {
  return {
    general: {
      battery_type: fields.battery_type,
      disable_suspend: fields.disable_suspend,
      log_to_sdcard: fields.log_to_sdcard,
      endpoint_url: fields.endpoint_url,
      rtc_timeout_seconds: fields.rtc_timeout_seconds,
      api_connection_timeout_seconds: fields.api_connection_timeout_seconds,
      verbose_debug: fields.verbose_debug,
      rtc_trigger_capture: fields.rtc_trigger_capture,
    },
    operation_mode: {
      name: fields.operation_mode_name,
      max_iso_day: fields.max_iso_day,
      max_iso_night: fields.max_iso_night,
      max_shutter_time_day: fields.max_shutter_time_day,
      max_shutter_time_night: fields.max_shutter_time_night,
      capture_jpeg_quality: fields.capture_jpeg_quality,
      capture_count: fields.capture_count,
      capture_delay_ms: fields.capture_delay_ms,
      process_sendmail: fields.process_sendmail,
      capture_mode: fields.capture_mode,
      trigger_black_out_period_ms: fields.trigger_black_out_period_ms,
      email_status_report: fields.email_status_report,
    },
    irled: {
      duty_cycle_still_cap: fields.duty_cycle_still_cap,
    },
    pir: {
      pir_sens: fields.pir_sens,
      pir_ignored: fields.pir_ignored,
      blind_time: fields.blind_time,
      pulse_counter: fields.pulse_counter,
      window_time: fields.window_time,
      operation_mode: fields.pir_operation_mode,
      filter_source: fields.filter_source,
      disable_pir_cron: fields.disable_pir_cron.split(',').map(s => s.trim()).filter(s => s),
      enable_pir_cron: fields.enable_pir_cron.split(',').map(s => s.trim()).filter(s => s),
    },
    lte: {
      apn: fields.apn,
      username: fields.lte_username,
      password: fields.lte_password,
      lte_ant_index: fields.lte_ant_index,
      wake_on_wan: fields.wake_on_wan,
      gps_enable: fields.gps_enable,
      reboot_delay: fields.reboot_delay,
      sim_index: fields.sim_index,
    },
    smtp: {
      host: fields.smtp_host,
      port: fields.smtp_port,
      username: fields.smtp_username,
      password: fields.smtp_password,
      from: fields.smtp_from,
      real_name: fields.smtp_real_name,
      to_addr: fields.smtp_to_addr.split(',').map(s => s.trim()).filter(s => s),
      to_cc: fields.smtp_to_cc.split(',').map(s => s.trim()).filter(s => s),
      to_bcc: fields.smtp_to_bcc.split(',').map(s => s.trim()).filter(s => s),
    },
    wifi: {
      sta_ssid: fields.sta_ssid,
      sta_password: fields.sta_password,
      wifi_ant_index: fields.wifi_ant_index,
    },
  };
}
